import { useState } from "react";
import { Clock, Share2, MapPin, ExternalLink, ChevronRight, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

// Article 2 hero: Tampa cable car above Brasov (Varianta A)
const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article2-v1-ZjmAs5X6RgtMKCGR8QmQdR.webp";
// Article 2 in-article: wooden hiking trail on Tampa mountain (Varianta B)
// In-article image: Piața Sfatului spring terrace (moved from Article 1)
const STREET_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v2-f98hYqM3XF5yrPbgC77gBX.webp";
// Tampa Mountain sign image for Food and drink section
const TAMPA_SIGN_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article2-tampa-sign-Cnksshj2N4HRWAE33YAxW5.webp";
const REALESTATE_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-real-estate-oVkqv2kDQCBzogKNZUAGJw.webp";

function SidebarAd() {
  return (
    <div className="border border-stone-200 overflow-hidden">
      <div className="px-3 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner Content · Cartier Transilvania</span>
      </div>
      <div className="relative">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania Brașov" className="w-full h-48 object-cover" loading="lazy" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.8) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-xs uppercase tracking-widest mb-1 font-semibold">New development · Noua</p>
          <h4 className="text-white font-bold leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
            Cartier Transilvania
          </h4>
        </div>
      </div>
      <div className="p-4">
        <p className="text-stone-600 text-sm font-light leading-relaxed mb-3">
          208 apartments in 6 blocks. T1–T3 completed &amp; occupied. T4–T6 delivering September 2027. Next to Lake Noua.
        </p>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-4">
          <MapPin size={11} />
          <span>Noua, Brașov</span>
        </div>
        <a
          href="https://www.cartiertransilvania.ro/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 text-xs text-white w-full px-3 py-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
          VIEW PROJECT <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

function InArticleBanner() {
  return (
    <div className="my-10 border border-stone-200 overflow-hidden">
      <div className="px-4 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col sm:flex-row">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full sm:w-48 h-36 object-cover flex-shrink-0" loading="lazy" />
        <div className="p-5 flex flex-col justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
              Apartments for sale in Brașov
            </p>
            <h4 className="font-bold text-stone-900 mb-2 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.05rem" }}>
              Cartier Transilvania — 208 apartments in Noua
            </h4>
            <p className="text-stone-500 text-sm font-light leading-relaxed">
              T1, T2, T3 completed and occupied. T4, T5, T6 under construction — delivery September 2027. Solar panels &amp; heat pump heating.
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <a
              href="https://www.cartiertransilvania.ro/en/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-white px-4 py-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
              VISIT PROJECT SITE <ChevronRight size={11} />
            </a>
            <span className="text-xs text-stone-400">cartiertransilvania.ro</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturedDeveloper() {
  return (
    <div className="mt-16 p-8 border-l-4" style={{ backgroundColor: "oklch(0.985 0.006 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full md:w-56 h-40 object-cover flex-shrink-0" loading="lazy" />
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
            Apartments for sale · Noua, Brașov
          </p>
          <h3 className="font-bold text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.3rem" }}>
            Cartier Transilvania — a development built to last
          </h3>
          <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.7" }}>
            208 apartments across 6 blocks in the Noua neighbourhood. T1, T2, and T3 are completed and occupied. T4, T5, and T6 are under construction with a delivery date of September 2027. Solar panels and heat pump heating system included. Next to Lake Noua, Brașov Zoo, and Parc Aventura.
          </p>
          <a
            href="https://www.cartiertransilvania.ro/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90"
            style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
            VISIT PROJECT SITE <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

const ARTICLE1_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
const ARTICLE3_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article3-residential-complex-Vstb4C43kK3pRH4WYGacpF.webp";
const ARTICLE4_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp";
const ARTICLE5_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-entertainment-hero-YwxKL9nvfxZqDmSm9YR8ym.webp";

const RELATED = [
  { tag: "City Guide", title: "Brașov, straight up: beyond the bear photos and the mountain air clichés", img: ARTICLE1_IMG, href: "/why-expats-choose-brasov" },
  { tag: "Relocation", title: "Cost of Living in Brașov 2026: the honest numbers", img: ARTICLE4_IMG, href: "/cost-of-living-brasov" },
  { tag: "Lifestyle", title: "Entertainment in Brașov: Day & Night for Adults — with Real Prices", img: ARTICLE5_IMG, href: "/entertainment-brasov-guide" },
];

function NewsletterInlineLight() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle"|"loading"|"success"|"error">("idle");
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch { setStatus("error"); }
  };
  if (status === "success") return <p className="text-xs text-green-700 py-2">✓ Subscribed! See you Friday.</p>;
  return (
    <form onSubmit={handleSubmit}>
      <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="w-full border border-stone-200 px-3 py-2 text-xs text-stone-700 outline-none focus:border-stone-400 mb-2" />
      <button type="submit" disabled={status === "loading"} className="w-full py-2 text-xs text-white font-semibold disabled:opacity-60" style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-500 mt-1">Try again.</p>}
    </form>
  );
}

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

export default function ArticleWhatToDo() {
  return (
    <>
    <Helmet>
      <title>What to Do in Brașov: The Complete City Guide for Expats</title>
      <meta name="description" content="What to do in Brașov: the complete guide — hiking, old town walks, day trips, cultural events, and hidden spots that most tourists miss." />
      <meta name="keywords" content="what to do in Brasov, things to do in Brasov Romania, Brasov city guide, Brasov activities, Brasov tourist guide" />
      <meta property="og:title" content="What to Do in Brașov: The Complete City Guide for Expats" />
      <meta property="og:description" content="What to do in Brașov: the complete guide — hiking, old town walks, day trips, cultural events, and hidden spots that most tourists miss." />
      <meta property="og:image" content="https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article2-v1-ZjmAs5X6RgtMKCGR8QmQdR.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://brasovgateway.com/what-to-do-in-brasov-city-guide" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="What to Do in Brașov: The Complete City Guide for Expats" />
      <link rel="canonical" href="https://brasovgateway.com/what-to-do-in-brasov-city-guide" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": "https://brasovgateway.com/what-to-do-in-brasov-city-guide#article",
              "headline": "What to do in Brașov: a guide for people who are tired of tourist guides",
              "description": "Practical guide to the best things to do in Brașov — hiking, old town, day trips, and local life beyond the tourist trail.",
              "image": "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp",
              "datePublished": "2026-04-01",
              "dateModified": "2026-04-28",
              "author": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "url": "https://brasovgateway.com/what-to-do-in-brasov-city-guide",
              "inLanguage": "en",
              "articleSection": "City Guide"
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://brasovgateway.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://brasovgateway.com/blog"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "What to do in Brașov: a guide for people who are tired of to...",
                  "item": "https://brasovgateway.com/what-to-do-in-brasov-city-guide"
                }
              ]
            }
          ]
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen" style={{ fontFamily: "Source Sans 3, sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
      {/* NAV */}
      <SiteHeader />

      {/* HERO IMAGE */}
      <div className="relative h-72 md:h-96 mt-16">
        <img src={HERO_IMG} alt="What to do in Brașov — Tampa cable car above the old town" className="w-full h-full object-cover" fetchPriority="high" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, oklch(0.985 0.006 75) 100%)" }} />
      </div>

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* MAIN CONTENT */}
          <main className="flex-1 min-w-0">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs uppercase tracking-widest font-semibold px-2 py-1" style={{ color: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.95 0.02 20)" }}>
                  City guide
                </span>
                <div className="flex items-center gap-1 text-stone-400 text-xs">
                  <Clock size={11} />
                  <span>6 min read</span>
                </div>
              </div>
              <h1 className="text-stone-900 mb-4 leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 700 }}>
                What to do in Brașov: a guide for people who are tired of tourist guides
              </h1>
              <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                    <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Brașov Gateway" className="w-full h-full object-cover opacity-50" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                    <p className="text-xs text-stone-500">Travel &amp; Lifestyle Team</p>
                  </div>
                </div>
                <ShareButton url="https://brasovgateway.com/what-to-do-in-brasov-city-guide" title="What to Do in Brașov: The Complete City Guide for Expats" />
              </div>
            </div>

            {/* ARTICLE BODY */}
            <div>
              <p className="text-stone-700 text-lg font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                If you're wondering what to do in Brașov, let's get the obvious out of the way first. Yes, there's a sign on the mountain. No, it doesn't say Hollywood. Yes, you should go up there at least once, preferably on a weekday when you're not sharing the cable car with three school groups and a man who brought his dog. The view is worth it. The selfie crowd is not.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                Now that we've settled that — let's talk about what Brașov actually is, beyond the Instagram highlights.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The old town: slower than you think, better than you expect
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                The Council Square (<em>Piața Sfatului</em>) is the kind of place that looks best at 7am, before the terraces fill up and the tour groups arrive with their matching caps. Get there early, order a coffee from one of the cafés on the square, and just sit. Watch the city wake up. <a href="/black-church-brasov-visitor-guide" className="text-amber-800 underline underline-offset-2 hover:text-amber-600">The Black Church</a> looms over the whole thing like it's been doing for six hundred years, entirely unbothered by the fact that you're taking a photo of it.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                The old town is small enough to walk in an hour, which means most people do exactly that — and miss everything worth finding. The real Brașov is in the streets behind the main square. Strada Sforii, officially one of the narrowest streets in Europe, is genuinely worth squeezing through. The Saxon fortifications, the bastions, the old walls that still circle the lower part of the city — these aren't museum pieces. They're just there, between apartment buildings and coffee shops, as if nobody thought it was particularly remarkable that they'd been standing since the 1400s.
              </p>

              <InArticleBanner />

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The mountains: not optional
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Brașov sits at the foot of the Bucegi and Piatra Craiului mountain ranges. This is not a scenic backdrop. This is the point. In summer, you have hundreds of kilometres of marked trails accessible within thirty minutes of the city centre. Postăvarul, the mountain that rises directly above the city, has trails for every level — from a gentle forest walk to a proper climb that will remind you how out of shape you've been since the pandemic. The cable car gets you to 1,800 metres if your legs have better things to do.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                In winter, Poiana Brașov is fifteen minutes away by car or bus. It's a proper ski resort — not enormous by Alpine standards, but well-maintained, affordable, and with the kind of après-ski atmosphere that doesn't require a second mortgage. If you've been paying ski resort prices in Austria or Switzerland, the first time you see a lift ticket price in Poiana Brașov you'll think there's been a printing error. <Link href="/cost-of-living-brasov#entertainment" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>The full breakdown of entertainment costs</Link> confirms it. And if you don't have a car, <Link href="/brasov-bus-guide" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>Linia 20 gets you there for 5 lei</Link>.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Day trips: the area around Brașov is the real secret
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Most people who visit Brașov don't realise they're sitting in the middle of one of the most historically dense regions in Europe.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">Bran Castle</strong> is twenty minutes away. Yes, it's the "Dracula Castle" — no, Dracula never actually lived there, and yes, the marketing department of Romanian tourism has been milking this for decades. Go anyway. The castle itself is genuinely interesting, the views are good, and the story of how a Transylvanian fortress became globally associated with a Victorian novel is worth knowing.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">Râșnov Fortress</strong> is even better, and half the tourists who come to Brașov somehow miss it. A medieval citadel on a hill, intact enough to walk through, with a view of the entire Bârsa depression spread out below you. Take a picnic.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">Sinaia</strong> is forty minutes south — a mountain town built around a royal palace, with a casino from the 1910s that looks like it was designed by someone who'd had too much champagne and access to a very large budget. The Peleș Castle there is, without exaggeration, one of the most impressive royal residences in Europe. Most people who visit Romania never see it because they go to Bucharest and stop there.
              </p>

              <figure className="my-10">
                <img src={STREET_IMG} alt="Piața Sfătului in spring, Brașov old town" className="w-full h-72 object-cover" loading="lazy" />
                <figcaption className="text-xs text-stone-400 mt-2 text-center">Piața Sfatului — the heart of Brașov's old town, where the city actually lives</figcaption>
              </figure>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Food and drink: what to actually eat
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Romanian food is not complicated. It's hearty, it's honest, and it's very good once you stop looking for it in tourist restaurants.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">Sarmale</strong> — cabbage rolls stuffed with minced meat and rice, slow-cooked for hours. The version you get at a proper Romanian restaurant, with sour cream and polenta on the side, is the kind of food that makes you understand why people stay in this country.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">Mici</strong> — grilled minced meat rolls, eaten standing up at a market stall, with mustard and bread. This is the Romanian equivalent of a hot dog, in the best possible way.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">Papanași</strong> — fried doughnuts with sour cream and jam. Order them at the end of a meal when you think you're not hungry. You will finish them. For coffee, Brașov has developed a genuine specialty coffee scene over the last few years. The old town has several good independent cafés that would hold their own in any European city. Skip the chains.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The thing nobody tells you
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Brașov is a city that rewards slowing down. The people who come for a weekend and leave thinking "nice place, a bit touristy" are the ones who spent all their time on the main square. The people who stay longer — or who come back — are the ones who figured out that the city has layers.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                The Saxon history, the communist-era architecture just outside the centre, the mix of Romanian, Hungarian and German cultural influences that shows up in the street names, the church denominations, the food — it takes more than two days to feel it. Which is, of course, the whole argument for not just visiting. If you want the unfiltered version of what life here is actually like, <Link href="/why-expats-choose-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>read our honest guide for expats</Link>.
              </p>

              {/* CTA */}
              <div className="mt-12 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.98 0.006 75)" }}>
                <p className="text-stone-600 font-light leading-relaxed text-sm">
                  <strong className="text-stone-900">Thinking about more than a visit?</strong> Brașov Gateway helps foreigners navigate everything from property purchase to company registration and relocation support.{" "}
                  <Link href="/#contact" className="font-semibold no-underline hover:underline" style={{ color: "oklch(0.38 0.12 15)" }}>
                    Get in touch
                  </Link>{" "}
                  — we know the city.
                </p>
              </div>

              <FeaturedDeveloper />
            </div>

            {/* RELATED ARTICLES */}
            <div className="mt-16 pt-10 border-t border-stone-200">
              <h3 className="text-stone-900 mb-8 text-xl font-bold" style={{ fontFamily: "Playfair Display, serif" }}>
                Continue reading
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {RELATED.map((r, i) => (
                  <Link key={i} href={r.href} className="group no-underline">
                    <div className="overflow-hidden mb-3">
                      <img src={r.img} alt={r.title} className="w-full h-36 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    </div>
                    <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "oklch(0.38 0.12 15)" }}>{r.tag}</span>
                    <h4 className="mt-2 text-stone-800 text-sm font-semibold leading-snug group-hover:text-stone-500 transition-colors" style={{ fontFamily: "Playfair Display, serif" }}>
                      {r.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>
          </main>

          {/* SIDEBAR */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-20 flex flex-col gap-6">
              <SidebarAd />
              <div className="border border-stone-200 p-5" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
                <h4 className="font-bold text-stone-900 mb-2 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>
                  About Brașov Gateway
                </h4>
                <p className="text-stone-500 text-xs font-light leading-relaxed">
                  We help foreigners move to, invest in, and build a life in Brașov. Real estate, company setup, relocation support — all in one place.
                </p>
                <Link href="/#contact" className="mt-4 flex items-center gap-1 text-xs font-semibold no-underline" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                  GET IN TOUCH <ChevronRight size={11} />
                </Link>
              </div>
              <div className="border border-stone-200 p-5">
                <h4 className="font-bold text-stone-900 mb-2 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>Stay in the loop</h4>
                <p className="text-stone-500 text-xs font-light leading-relaxed mb-3">No spam. One email per week with what is actually happening in Brașov.</p>
                <NewsletterInlineLight />
              </div>
              {/* CTA card — Relocation support */}
              <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Relocation support</p>
                <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                  Moving to Brașov?
                </h4>
                <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                  We help expats find apartments, navigate bureaucracy, and settle in — without the stress.
                </p>
                <Link href="/contact">
                  <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                    BOOK A FREE CALL
                  </button>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <SiteFooter />
    </div>
    </>
  );
}
